// ダッシュボード: ログイン済みのホストが過去セッション一覧 + 研修テンプレートを管理する。
// 未ログイン (401) の場合は /login にリダイレクト。

import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { t, type Locale } from '@solo-webinar-copilot/i18n';
import type { Session, TrainingTemplate } from '@solo-webinar-copilot/shared';
import {
  createTemplate,
  deleteTemplate,
  fetchMe,
  fetchSessions,
  fetchTemplates,
  logoutUrl,
  setSessionTemplate,
  updateMyLanguage,
  type MeUser,
} from './lib/api.ts';

export function Dashboard({ locale }: { locale: Locale }): ReactElement {
  const [me, setMe] = useState<MeUser | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [templates, setTemplates] = useState<TrainingTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const [m, s, t2] = await Promise.all([
      fetchMe(),
      fetchSessions(),
      fetchTemplates(),
    ]);
    if (!m.ok) {
      // 401 / unauthenticated → /login へ
      if (typeof window !== 'undefined' && /401|unauth/i.test(m.error)) {
        window.location.assign('/login');
        return;
      }
      setError(m.error);
      setLoading(false);
      return;
    }
    setMe(m.data.user);
    if (s.ok) setSessions(s.data.items);
    if (t2.ok) setTemplates(t2.data.items);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (loading) return <main data-testid="dashboard-loading"><p>…</p></main>;
  if (error || !me) {
    return (
      <main data-testid="dashboard-error">
        <p role="alert">{t('web.error.network', locale)}: {error}</p>
      </main>
    );
  }

  return (
    <main data-testid="dashboard">
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div>
          <h1>{t('web.dashboard.title', locale)}</h1>
          <p>
            {t('web.dashboard.welcome', locale)}: {me.displayName ?? me.email}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
          <label>
            {t('web.dashboard.language', locale)}{' '}
            <select
              value={me.language}
              data-testid="dashboard-language"
              onChange={async (e) => {
                const next = e.currentTarget.value as 'ja' | 'en';
                const r = await updateMyLanguage(next);
                if (r.ok) {
                  setMe({ ...me, language: next });
                  // UI の locale は currently DEFAULT_LOCALE 固定なので再読み込みで反映。
                  if (typeof window !== 'undefined') window.location.reload();
                } else {
                  setError(r.error);
                }
              }}
            >
              <option value="ja">日本語</option>
              <option value="en">English</option>
            </select>
          </label>
          <a href={logoutUrl()} data-testid="dashboard-logout">
            {t('web.dashboard.logout', locale)}
          </a>
        </div>
      </header>

      <section data-testid="dashboard-sessions">
        <h2>{t('web.dashboard.sessions', locale)}</h2>
        {sessions.length === 0 ? (
          <p>{t('web.dashboard.no_sessions', locale)}</p>
        ) : (
          <ul>
            {sessions.map((s) => (
              <li
                key={s.id}
                data-testid={`dashboard-session-${s.id}`}
                style={{ marginBottom: 6 }}
              >
                <a href={`/reports/${encodeURIComponent(s.id)}`}>
                  {s.zoom_meeting_id}
                </a>{' '}
                <small>[{s.status}]</small>
                {s.started_at ? (
                  <small style={{ marginLeft: 6, opacity: 0.7 }}>
                    {new Date(s.started_at).toISOString().slice(0, 16)}
                  </small>
                ) : null}
                {' · '}
                <label>
                  {t('web.dashboard.template', locale)}{' '}
                  <select
                    value={s.template_id ?? ''}
                    data-testid={`dashboard-session-template-${s.id}`}
                    onChange={async (e) => {
                      const newId = e.currentTarget.value || null;
                      const r = await setSessionTemplate(s.id, newId);
                      if (r.ok) {
                        setSessions((prev) =>
                          prev.map((x) =>
                            x.id === s.id ? { ...x, template_id: newId } : x,
                          ),
                        );
                      } else {
                        setError(r.error);
                      }
                    }}
                  >
                    <option value="">
                      {t('web.dashboard.template_none', locale)}
                    </option>
                    {templates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.name}
                      </option>
                    ))}
                  </select>
                </label>
              </li>
            ))}
          </ul>
        )}
      </section>

      <TemplatesSection
        locale={locale}
        items={templates}
        onCreate={async (draft) => {
          const r = await createTemplate(draft);
          if (r.ok) {
            setTemplates((prev) => [r.item, ...prev]);
            return null;
          }
          return r.error;
        }}
        onDelete={async (id) => {
          const r = await deleteTemplate(id);
          if (r.ok) setTemplates((prev) => prev.filter((x) => x.id !== id));
          else setError(r.error);
        }}
      />
    </main>
  );
}

interface TemplatesSectionProps {
  locale: Locale;
  items: TrainingTemplate[];
  onCreate: (draft: { name: string; description?: string }) => Promise<string | null>;
  onDelete: (id: string) => Promise<void>;
}

function TemplatesSection({
  locale,
  items,
  onCreate,
  onDelete,
}: TemplatesSectionProps): ReactElement {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handle = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSubmitting(true);
    setFormError(null);
    const draft: { name: string; description?: string } = { name: name.trim() };
    const d = description.trim();
    if (d) draft.description = d;
    const err = await onCreate(draft);
    setSubmitting(false);
    if (err) setFormError(err);
    else {
      setName('');
      setDescription('');
      setShowForm(false);
    }
  };

  return (
    <section data-testid="dashboard-templates">
      <h2>{t('web.dashboard.templates', locale)}</h2>
      {items.length === 0 ? (
        <p>{t('web.dashboard.no_templates', locale)}</p>
      ) : (
        <ul>
          {items.map((tpl) => (
            <li key={tpl.id} data-testid={`dashboard-template-${tpl.id}`}>
              <strong>{tpl.name}</strong>
              {tpl.description ? `: ${tpl.description}` : null}{' '}
              <button
                type="button"
                onClick={() => void onDelete(tpl.id)}
                data-testid={`dashboard-template-delete-${tpl.id}`}
              >
                {t('web.dashboard.delete', locale)}
              </button>
            </li>
          ))}
        </ul>
      )}
      {showForm ? (
        <form onSubmit={handle} data-testid="dashboard-template-form">
          <div>
            <label>
              {t('web.dashboard.template_name', locale)}
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.currentTarget.value)}
                data-testid="dashboard-template-name"
                required
              />
            </label>
          </div>
          <div>
            <label>
              {t('web.dashboard.template_description', locale)}
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                data-testid="dashboard-template-description"
              />
            </label>
          </div>
          {formError ? (
            <p role="alert" data-testid="dashboard-template-error">
              {formError}
            </p>
          ) : null}
          <div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="dashboard-template-save"
            >
              {t('web.dashboard.save', locale)}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              data-testid="dashboard-template-cancel"
            >
              {t('web.dashboard.cancel', locale)}
            </button>
          </div>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setShowForm(true)}
          data-testid="dashboard-template-add"
        >
          {t('web.dashboard.add_template', locale)}
        </button>
      )}
    </section>
  );
}
